import Button from '@jetbrains/ring-ui/components/button/button'
import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import Panel from '@jetbrains/ring-ui/components/panel/panel'
import * as React from 'react'
import type {PreloadedQuery, UseQueryLoaderLoadQueryOptions} from 'react-relay'
import {usePreloadedQuery, useQueryLoader} from 'react-relay'
import AutoSizer from 'react-virtualized/dist/es/AutoSizer'
import type {ListRowProps} from 'react-virtualized/dist/es/List'
import List from 'react-virtualized/dist/es/List'

import {stringifyId} from '../../../../../../../types'
import type {AgentPoolId} from '../../../../../../../types'
import {emptyArray, emptyObject} from '../../../../../../../utils/empty'
import type {KeyValue, WritableKeyValue} from '../../../../../../../utils/object'
import {objectKeys} from '../../../../../../../utils/object'
import {subscribeOnOverallEvents} from '../../../../../../../utils/subscriber'
import {OVERALL_AGENT_POOL_EVENT_TOPICS} from '../../../../AgentsPages.actions'
import Search from '../../../../components/Search/Search'
import type {
  AgentPoolPageProjectsQuery,
  AgentPoolPageProjectsQuery$data,
} from '../../../__generated__/AgentPoolPageProjectsQuery.graphql'
import {useTreeState, SEARCHABLE_PROJECTS_NUMBER} from '../../../AgentPoolPage.hooks'
import {projectsTreeQuery} from '../../../AgentPoolPage.queries'
import {createProjectsTree, getFlatProjectsTree} from '../../../AgentPoolPage.selectors'
import type {AgentPoolProjectsTreeProjectItem} from '../../../AgentPoolPage.types'
import {matchName} from '../../../AgentPoolPage.utils'

import AssignProjectsButton from './AssignProjectsButton/AssignProjectsButton'
import ProjectsTreeItem from './ProjectsTreeItem/ProjectsTreeItem'

import styles from './AssignProjectsDialog.css'

type DataContainerProps = {
  agentPoolId: AgentPoolId
  onCloseAttempt: () => void
}

type ContentProps = {
  agentPoolId: AgentPoolId
  onCloseAttempt: () => void
  queryRef: PreloadedQuery<AgentPoolPageProjectsQuery>
} & DataContainerProps

const STRING_ROOT_PROJECT_ID = '_Root'

const SEARCH_INPUT_ID = 'ASSIGN_PROJECTS_DIALOG_SEARCH'

type ProjectSelectedState = {selected: boolean}
type ProjectsTreeSelectedState = KeyValue<string, ProjectSelectedState>

const getItemSelectedState = (
  state: ProjectsTreeSelectedState,
  projectId: string,
): ProjectSelectedState => state[projectId] || {selected: false}

function getSelectedProjectIds(selectedState: ProjectsTreeSelectedState) {
  return objectKeys(selectedState).reduce((list: string[], id) => {
    const item = selectedState[id]
    return item?.selected ? [...list, id] : list
  }, [])
}

function isItemAssignable({
  permissions,
  id,
  assigned,
  archived,
}: AgentPoolProjectsTreeProjectItem): boolean {
  return (
    permissions?.manageAgentPoolsForProject === true &&
    id !== STRING_ROOT_PROJECT_ID &&
    !assigned &&
    !archived
  )
}

const NO_PERMISSION_HINT = 'You don`t have the permission to assign the project'

function getDisabledHint({permissions}: AgentPoolProjectsTreeProjectItem): string | null {
  if (permissions == null || permissions.manageAgentPoolsForProject !== true) {
    return NO_PERMISSION_HINT
  }
  return null
}

function walkProjectsTreeDown<T>(
  projectItem: AgentPoolProjectsTreeProjectItem,
  callback: (projectItem: AgentPoolProjectsTreeProjectItem, accumulator: T) => void,
  accumulator: T,
) {
  for (let i = 0, l = projectItem.children.length; i < l; i++) {
    callback(projectItem.children[i], accumulator)
    walkProjectsTreeDown(projectItem.children[i], callback, accumulator)
  }
  callback(projectItem, accumulator)
}

const ROW_HEIGHT = 24

const INITIAL_EXPAND_STATE = {
  [STRING_ROOT_PROJECT_ID]: true,
}

function AssignProjectsDialogContent({queryRef, agentPoolId, onCloseAttempt}: ContentProps) {
  const $data: AgentPoolPageProjectsQuery$data = usePreloadedQuery(projectsTreeQuery, queryRef)
  const projectEdges = $data.projects?.edges || emptyArray
  const [expandState, setExpandState, searchQuery, setSearchQuery] =
    useTreeState(INITIAL_EXPAND_STATE)
  const [selectedState, setSelectedState] = React.useState<ProjectsTreeSelectedState>(emptyObject)
  const [exclusively, setExclusively] = React.useState(false)

  React.useEffect(
    () => () => {
      setSelectedState(emptyObject)
    },
    [],
  )

  const onProjectSelect = React.useCallback(
    (item: AgentPoolProjectsTreeProjectItem) => {
      const state = {...selectedState}
      const itemState = {...getItemSelectedState(state, item.rawId)}

      const itemTreeState: WritableKeyValue<string, ProjectSelectedState> = {}
      const acc = {equal: true}

      // Invert the item's selection
      // Invert the item's descendants' selection if all of them have state the same as the item

      // Walking the tree to
      //   1. Figure out if the state of items is equal
      //   2. Calculate the state of the descendants to be used in case of the equality
      walkProjectsTreeDown<{equal: boolean}>(
        item,
        (currentItem, accumulator) => {
          const currentItemState = state[currentItem.rawId]
          if (currentItemState != null && currentItemState.selected !== itemState.selected) {
            accumulator.equal = false
          }

          if (isItemAssignable(item)) {
            itemTreeState[currentItem.rawId] = {
              selected: !itemState.selected,
            }
          }
        },
        acc,
      )

      // Saving the state depending of the equality
      setSelectedState({
        ...state,
        ...(acc.equal ? itemTreeState : {[item.rawId]: itemTreeState[item.rawId]}),
      })
    },
    [selectedState, setSelectedState],
  )
  const onSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.currentTarget.value),
    [setSearchQuery],
  )
  const onSearchClear = React.useCallback(() => setSearchQuery(''), [setSearchQuery])
  const stringAgentPoolId = stringifyId(agentPoolId)

  const projectsTree = React.useMemo(() => createProjectsTree(projectEdges), [projectEdges])
  const flatProjectsTree = React.useMemo(
    () =>
      getFlatProjectsTree(
        projectsTree,
        expandState,
        stringAgentPoolId,
        ({name}, parentIsMatching) => parentIsMatching || matchName(name, searchQuery),
      ).filter(item => isItemAssignable(item)),
    [expandState, projectsTree, searchQuery, stringAgentPoolId],
  )

  const rowRenderer = React.useCallback(
    ({index, style}: ListRowProps) => {
      const treeItem = flatProjectsTree[index]
      const {rawId} = treeItem

      return (
        <div className={styles.treeItem} key={rawId} style={style} role="row">
          <ProjectsTreeItem
            className={styles.treeItemProject}
            searchQuery={searchQuery}
            agentPoolId={stringAgentPoolId}
            onToggleExpand={setExpandState}
            selected={selectedState[rawId]?.selected}
            onSelect={onProjectSelect}
            disabledHint={getDisabledHint(treeItem)}
            item={treeItem}
          />
        </div>
      )
    },
    [
      flatProjectsTree,
      onProjectSelect,
      searchQuery,
      selectedState,
      setExpandState,
      stringAgentPoolId,
    ],
  )

  const onExclusivelyChange = React.useCallback(() => {
    setExclusively(!exclusively)
  }, [setExclusively, exclusively])
  const selectedProjectIds = React.useMemo(
    () => getSelectedProjectIds(selectedState),
    [selectedState],
  )
  const onAssign = React.useCallback(() => {
    onCloseAttempt()
  }, [onCloseAttempt])

  return (
    <>
      {searchQuery !== '' || flatProjectsTree.length > SEARCHABLE_PROJECTS_NUMBER ? (
        <Search
          className={styles.searchInput}
          onChange={onSearchChange}
          onClear={onSearchClear}
          id={SEARCH_INPUT_ID}
          value={searchQuery}
          placeholder={'Search projects...'}
        />
      ) : null}
      {searchQuery !== '' || flatProjectsTree.length > 0 ? (
        <div className={styles.tree}>
          <AutoSizer>
            {({width, height}) => (
              <List
                width={width}
                height={height}
                rowRenderer={rowRenderer}
                rowCount={flatProjectsTree.length}
                rowHeight={ROW_HEIGHT}
                flatTree={flatProjectsTree}
              />
            )}
          </AutoSizer>
        </div>
      ) : (
        <div className={styles.emptyTree}>{'No assignable projects'}</div>
      )}
      <Panel className={styles.panel}>
        <Checkbox
          checked={exclusively}
          onChange={onExclusivelyChange}
          containerClassName={styles.diassociateCheckbox}
          disabled={selectedProjectIds.length === 0}
        >
          {'Unassign projects from other pools'}
        </Checkbox>
        <AssignProjectsButton
          agentPoolRawId={stringAgentPoolId}
          projectRawIds={selectedProjectIds}
          exclusively={exclusively}
          onAssign={onAssign}
          disabled={selectedProjectIds.length === 0}
        />
        <Button onClick={onCloseAttempt}>{'Cancel'}</Button>
      </Panel>
    </>
  )
}

const loader = <LoaderInline className={styles.loader} />

const projectsTreeQueryVars = {
  withPermissions: true,
}
const projectsTreeQueryFetchOptions: UseQueryLoaderLoadQueryOptions = {
  fetchPolicy: 'store-and-network',
}

function AssignProjectsDialogDataContainer(props: DataContainerProps) {
  const [projectsTreeQueryRef, loadProjectsTreeQuery, disposeProjectsTreeQuery] =
    useQueryLoader<AgentPoolPageProjectsQuery>(projectsTreeQuery)
  React.useEffect(
    () =>
      subscribeOnOverallEvents(OVERALL_AGENT_POOL_EVENT_TOPICS, () =>
        loadProjectsTreeQuery(projectsTreeQueryVars, projectsTreeQueryFetchOptions),
      ),
    [loadProjectsTreeQuery],
  )
  React.useEffect(() => () => disposeProjectsTreeQuery(), [disposeProjectsTreeQuery])
  return projectsTreeQueryRef != null ? (
    <React.Suspense fallback={loader}>
      <AssignProjectsDialogContent queryRef={projectsTreeQueryRef} {...props} />
    </React.Suspense>
  ) : null
}

export default React.memo(AssignProjectsDialogDataContainer)
